import * as React from "react";
import { useState } from "react";
import { useParams, Link as RouterLink } from "react-router-dom";
import { Alert, AlertTitle, Link, Stack, Typography } from "@mui/material";
import { SimpleBreadcrumbs } from "../../../components/navigation/simpleBreadcrumbs";
import StandardLayout from "../../../layouts/standard";
import { useGetCclParticipantQuery } from "../../../services/cclTokenedEnterpriseParticipantApi";
import PageLoader from "../../../components/common/pageLoader";
import DetailPage, { DetailPageTab } from "../../../components/common/detailPage/detailPage";
import RegistrationGeneralPanel from "./general/registrationGeneralPanel";
import { Participant } from "../../../services/types/enterpriseParticipantApiTypes";
import { useGetCclEventByEventKeyQuery } from "../../../services/cclTokenedSessionApi";
import { getErrorMsg } from "../../../utilities/rtkQueryErrorHelpers";
import useCombinationKeyPress from "../../../hooks/useCombinationKeyPress";
import RegistrationJournalDrawer from "./registrationJournalDrawer";
import {
    UserClaimsService,
    eGroupIds,
} from "../../../services/currentUserService/currentUserService";
import IndividualHistoryPanel from "../../../components/shared/individualHistoryPanel/individualHistoryPanel";
import { RouteAccess } from "../../../app/slices/allowedRoutesSlice";
import RegistrationAssessmentsPanel from "./assessments/registrationAssessmentsPanel";
import ScoringSnapshot from "../../assessments/scoringSnapshot";
import { AP_SURVEY_PLATFORM_KEYS } from "../../../services/types/sessionDetailTypes";

export type RegistrationDetailProps = {
    esiKey: string;
    tabIndex?: string;
};

const RegistrationDetail: React.FC = (props) => {
    const params = useParams<RegistrationDetailProps>();
    const esikey = params.esiKey !== undefined ? decodeURIComponent(params.esiKey) : "";
    const tabIndex: number =
        params.tabIndex !== undefined && !isNaN(Number(params.tabIndex)) ? +params.tabIndex : 0;

    const [esKey, setEsKey] = useState<string>("");
    const [drawerOpen, setDrawerOpen] = React.useState<boolean>(false);
    const [journalKey, setJournalKey] = React.useState<number>(Date.now());
    const [activeTabIndex, setActiveTabIndex] = useState<number>(tabIndex);

    const claimsService = new UserClaimsService();
    const isUserClientAdmin = claimsService.IsUserInGroups([eGroupIds.eClientAdmin]);

    const {
        data: queryregistration,
        isLoading: regIsLoading,
        isSuccess: regIsSuccess,
        isError: regIsError,
        error: regError,
    } = useGetCclParticipantQuery(esikey, { skip: esikey === "" });
    const {
        data: session,
        isLoading: sessionIsLoading,
        isSuccess: sessionIsSuccess,
        isError: sessionIsError,
        error: sessionError,
    } = useGetCclEventByEventKeyQuery(esKey, { skip: esKey === "" });
    const detailEl = React.useRef<HTMLDivElement>(null);

    const allowJournalEntries =
        claimsService.IsUserCclAdminOrSuperAdmin() ||
        claimsService.IsUserNwaAdmin() ||
        claimsService.IsUserInGroups([eGroupIds.eClientAdmin]);

    const onKeyDown = (event: KeyboardEvent) => {
        setDrawerOpen(true);
        event.preventDefault();
    };
    useCombinationKeyPress(["j"], onKeyDown, detailEl.current);

    const emptyRegistration = {} as Participant;
    const registration =
        queryregistration == null
            ? emptyRegistration
            : JSON.parse(JSON.stringify(queryregistration));

    const isPartnerSession = session?.networkPartner !== "N/A";
    const isApSurveyPlatform = AP_SURVEY_PLATFORM_KEYS.includes(
        session?.assessmentPlatformKey ?? 0,
    );

    const [esiKeys, setEsiKeys] = useState<string[]>([]);

    React.useEffect(() => {
        if (!params.esiKey) return;
        let temp = esiKeys;
        temp.push(params.esiKey);
        setEsiKeys(temp);
        //eslint-disable-next-line
    }, [params]);

    const handleJournalEntry = (keys: string[]) => {
        let temp = esiKeys;
        temp.push(...keys);
        setEsiKeys(temp);
        setDrawerOpen(true);
    };

    const tabs: DetailPageTab[] = [
        {
            label: "Details",
            index: 0,
            saveable: false,

            content: (
                <RegistrationGeneralPanel
                    registration={registration}
                    isNwaSession={isPartnerSession}
                    isClientAdmin={isUserClientAdmin}
                />
            ),
        },
        {
            label: "Assessments",
            index: 1,
            saveable: true,
            visible:
                isApSurveyPlatform &&
                claimsService.IsUserInGroups([
                    eGroupIds.eCCLSuperAdmin,
                    eGroupIds.eCCLAdmin,
                    eGroupIds.eNWAAdmin,
                    eGroupIds.eClientAdmin,
                ]),
            content: (
                <RegistrationAssessmentsPanel
                    sessionDetails={session}
                    registration={registration ?? undefined}
                    handleJournalEntry={handleJournalEntry}
                    clientAdmin={isUserClientAdmin}
                />
            ),
        },
        {
            label: "Scoring",
            index: 3,
            saveable: false,
            visible:
                isApSurveyPlatform &&
                claimsService.IsUserInGroups([
                    eGroupIds.eCCLSuperAdmin,
                    eGroupIds.eCCLAdmin,
                    eGroupIds.eNWAAdmin,
                    eGroupIds.eClientAdmin,
                ]),
            content: <ScoringSnapshot fromParticipantDetail={true} esiKey={registration?.esiKey} />,
        },
        {
            label: "Logs",
            index: 2,
            saveable: false,
            content: (
                <IndividualHistoryPanel
                    journalTabKey={journalKey}
                    imKey={registration?.imKey?.toString() ?? ""}
                    emailAddress={registration?.emailAddress}
                    idPrefix="registration-detail"
                    onCreateJournalEntry={() => setDrawerOpen(true)}
                    onlyRegistrationEvents={true}
                    esiKey={registration?.esiKey?.toString() ?? ""}
                />
            ),
        },
    ];

    React.useEffect(() => {
        if (!sessionIsSuccess) return;
        const visibleIndices = tabs
            .filter((t) => t.visible !== false)
            .map((t) => t.index);
        if (visibleIndices.length > 0 && !visibleIndices.includes(activeTabIndex)) {
            setActiveTabIndex(visibleIndices[0]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sessionIsSuccess, isApSurveyPlatform]);

    function showErrorAlert(msg: string | undefined) {
        return (
            <StandardLayout breadcrumbs={breadcrumbs}>
                <div>
                    <Alert severity="error" sx={{ fontSize: 14 }}>
                        <AlertTitle sx={{ fontSize: 14 }}>Error</AlertTitle>
                        {msg}
                    </Alert>
                </div>
            </StandardLayout>
        );
    }

    function onSaveJournalEntry() {
        setJournalKey(Date.now());
        setDrawerOpen(false);
    }

    const allowedRoutes: RouteAccess = claimsService.GetCurrentAllowedRoutes();

    const breadcrumbs = (
        <Stack direction={"row"} spacing={3}>
            <SimpleBreadcrumbs>
                {allowedRoutes.allowRegistrations ? (
                    <Typography variant="h6" color="text.primary">
                        <Link key="findRegistrationLink" component={RouterLink} to="/registrations">
                            Registrations
                        </Link>
                    </Typography>
                ) : (
                    <Typography variant="h6" color="text.primary">
                        Registrations
                    </Typography>
                )}
                <Typography variant="h6" color="text.primary">
                    {registration.esiKey === undefined
                        ? ""
                        : registration.firstName + " " + registration.lastName}
                </Typography>
            </SimpleBreadcrumbs>
        </Stack>
    );

    if (regIsLoading || sessionIsLoading) {
        return (
            <StandardLayout breadcrumbs={breadcrumbs}>
                <PageLoader msg={""} />
            </StandardLayout>
        );
    }

    if (regIsSuccess) {
        if (esKey === "") {
            setEsKey(queryregistration.esKey.toString());
        } else if (sessionIsSuccess) {
            registration.session = JSON.parse(JSON.stringify(session));
            if (registration != null && session != null) {
                return (
                    <div ref={detailEl}>
                        {allowJournalEntries && (
                            <RegistrationJournalDrawer
                                registrations={[registration]}
                                sessionCode={session?.sessionCode ?? ""}
                                showCountBanner={false}
                                open={drawerOpen}
                                onClose={() => setDrawerOpen(false)}
                                onSave={() => onSaveJournalEntry()}
                            />
                        )}
                        <DetailPage
                            descriptor={"registration"}
                            breadcrumbs={breadcrumbs}
                            tablist={tabs}
                            activeTab={activeTabIndex}
                            handleTabChange={(evt, index: number) => setActiveTabIndex(index)}
                            showSavePreferences={true}
                            preferencesArea="registrationDetail"
                        />
                    </div>
                );
            }
        } else if (!sessionIsError) {
            return showErrorAlert(`No registration data found for registration ${esikey}`);
        }
    }

    if (regIsError || sessionIsError) {
        var msg = regIsError ? getErrorMsg(regError) : getErrorMsg(sessionError);
        console.log(msg);
        return showErrorAlert(
            `Error retrieving registration data for registration key ${esikey} (${msg})`,
        );
    }

    return showErrorAlert(
        `Unknown error retrieving registration data for registration key ${esikey}`,
    );
};

export default RegistrationDetail;
